// Read with AI: sends a purchase agreement to the contract reader (the TitleCON demo backend in the
// sandbox) and maps its reading onto this form. Ported from the reader's client/contract-reader.js.
// Shown only on the logged-in form, for companies whose OrderRequestFeature has ContractReaderEnabled.
// The reader's addresses come from GetConfig at runtime (contractReader), which the portal API sends only
// to those companies, so they're never built into the app.
import type { ContractReaderConfig } from '../../../types/portalTypes/api/GetConfig'
import { LenderInfo, OrganizationType, PartyFields, PersonInfo } from '../../../types/orderRequest'
import { ORGANIZATION_TYPES, PROPERTY_TYPES, US_STATES } from './OrderRequestFormComponents'

/** The reader to use, or null to hide the panel: this company has the demo turned on (its OrderRequestFeature
 *  config) and GetConfig said where the reader lives. */
export function contractReaderFor(feature: { ContractReaderEnabled?: boolean } | undefined,
  config: Partial<ContractReaderConfig> | undefined): ContractReaderConfig | null {
  const url = (config?.url ?? '').trim().replace(/\/$/, '')
  const uploadHost = (config?.uploadHost ?? '').trim().toLowerCase()
  // Checked once here, so nothing below ever talks to an address that isn't a bare https origin.
  return feature?.ContractReaderEnabled && isSafeBase(url) && uploadHost ? { url, uploadHost } : null
}

export type Confidence = 'high' | 'medium' | 'low' | null

export type ReaderField = { value: any; normalized?: any; page: number | null; evidence?: string; confidence?: Confidence }
export type ReaderParty = {
  type: 'individual' | 'married' | 'organization'
  name: string
  spouse?: string
  organization_type?: string
  contact_people?: string[]
  email?: string
  phone?: string
  page: number | null
  evidence?: string
  /** How sure the AI was of the names and grouping. Missing from older readings. */
  confidence?: Confidence
}
export type ReaderAgent = { role: string; name: string; company?: string; email?: string; phone?: string; page: number | null }
export type Reading = {
  fields: Record<string, ReaderField>
  buyers: ReaderParty[]
  sellers: ReaderParty[]
  agents: ReaderAgent[]
  concerns: { message: string; page: number | null }[]
  stats?: { seconds?: number }
}
/** The reader's /api/health: whether it wants a passcode. */
export async function readerNeedsPasscode(baseUrl: string): Promise<boolean> {
  const base = baseUrl.replace(/\/$/, '')
  // Same rule as readContract: a cleartext answer could hide the passcode field, so don't ask one.
  if (!isSafeBase(base)) throw new Error('The contract reader address must be https (or http://localhost).')
  const r = await fetch(`${base}/api/health`)
  // A failed check (say a 503) throws, so the panel falls back to asking for the passcode.
  if (!r.ok) throw new Error(`Health check failed (${r.status})`)
  const data = await r.json()
  return Boolean(data.passcode)
}

type ReadOptions = {
  /** The reader's API base (GetConfig's contractReader.url). */
  baseUrl: string
  /** The reader's upload bucket host (GetConfig's contractReader.uploadHost). */
  uploadHost: string
  passcode?: string
  onProgress?: (text: string) => void
  /** Called when the order form part is read (about 25 seconds). */
  onReading: (reading: Reading) => void
  signal?: AbortSignal
}

/** Uploads the agreement (one PDF, or a photo of each page) and polls until the reading is done. */
export async function readContract(files: File[], { baseUrl, uploadHost, passcode, onProgress = () => {}, onReading, signal }: ReadOptions): Promise<void> {
  const base = baseUrl.replace(/\/$/, '')
  if (!isSafeBase(base)) throw new Error('The contract reader address must be https (or http://localhost).')
  const api = async (path: string, body?: unknown) => {
    const r = await fetch(`${base}${path}`, body === undefined ? { signal } : {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal,
    })
    const data = await r.json().catch(() => ({}))
    if (!r.ok) throw Object.assign(new Error(data.error || `Request failed (${r.status})`), { status: r.status })
    return data
  }

  onProgress('Uploading…')
  const { job, uploads } = await api('/api/jobs', {
    files: files.map((f, i) => ({ name: `page-${i + 1}`, type: f.type, size: f.size })), passcode: passcode || undefined,
    // Only the form fields: the reader skips its full reading, so it's one AI request (about a cent).
    details: false,
  })
  const ups = uploads as { url: string; fields: Record<string, string> }[]
  // The file goes only where the reader's own S3 bucket takes uploads, one upload per file.
  if (!Array.isArray(ups) || ups.length !== files.length || !ups.every((u) => isUploadUrl(u?.url, uploadHost))) {
    throw new Error('The contract reader gave an unexpected upload address, so nothing was sent.')
  }
  for (let i = 0; i < ups.length; i++) {
    const up = ups[i]
    const form = new FormData()
    for (const [k, v] of Object.entries(up.fields)) form.append(k, v)
    form.append('file', files[i])
    const r = await fetch(up.url, { method: 'POST', body: form, signal })
    if (!r.ok) throw new Error('Upload failed. Please try again.')
  }
  await api(`/api/jobs/${job}/start`, {})

  onProgress('Reading the agreement…')
  const started = Date.now()
  let misses = 0
  for (;;) {
    await wait(1500, signal)
    let j: any
    try {
      j = await api(`/api/jobs/${job}`)
      misses = 0
    } catch (e: any) {
      // The reading carries on on the server, so a dropped connection or a busy moment is retried.
      const retryable = !e.status || e.status === 429 || e.status >= 500
      if (!retryable || signal?.aborted || ++misses >= 5) throw e
      continue
    }
    const late = Date.now() - started > 16 * 60 * 1000
    if (j.status === 'failed') throw new Error(j.error || 'Reading failed.')
    if (j.status === 'done') {
      onReading(j.result)
      return
    }
    if (late) throw new Error('The reading took too long.')
  }
}

/** A bare origin (no user, path, query or fragment; a trailing slash is fine): https on the default port,
 *  or http://localhost for a developer's own copy. */
export function isSafeBase(url: string): boolean {
  try {
    const u = new URL(url)
    if (u.username || u.password || u.pathname !== '/' || u.search || u.hash || url.includes('?') || url.includes('#')) return false
    return (u.protocol === 'https:' && !u.port) || (u.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(u.hostname))
  } catch {
    return false
  }
}

/** Exactly the reader's own upload bucket, over https; any other bucket or host is refused. */
export function isUploadUrl(url: unknown, host: string): boolean {
  try {
    const u = new URL(String(url))
    return Boolean(host) && u.protocol === 'https:' && u.hostname === host && !u.username && !u.password && (u.port === '' || u.port === '443')
  } catch {
    return false
  }
}

function wait(ms: number, signal?: AbortSignal) {
  return new Promise<void>((ok, no) => {
    if (signal?.aborted) return no(signal.reason)
    const t = setTimeout(ok, ms)
    signal?.addEventListener('abort', () => { clearTimeout(t); no(signal.reason) }, { once: true })
  })
}

/** "20261120" (the form's date format) from "2026-11-20"; "" for anything that isn't a real calendar date. */
export const formDate = (iso: unknown) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso ?? ''))
  if (!m) return ''
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  const real = d.getUTCFullYear() === Number(m[1]) && d.getUTCMonth() === Number(m[2]) - 1 && d.getUTCDate() === Number(m[3])
  return real ? `${m[1]}${m[2]}${m[3]}` : ''
}
const showDate = (v: string) => `${v.slice(4, 6)}/${v.slice(6)}/${v.slice(0, 4)}`

/** First, middle and last name from a full name. Suffixes like "Jr." need a person's eye. */
export function splitName(full: unknown): PersonInfo {
  const parts = String(full ?? '').trim().split(/\s+/).filter(Boolean)
  if (parts.length < 2) return { FirstName: parts[0] ?? '', MiddleName: '', LastName: '', Email: '', Phone: '' }
  return { FirstName: parts[0], MiddleName: parts.slice(1, -1).join(' '), LastName: parts[parts.length - 1], Email: '', Phone: '' }
}

/** True only for a plain statement that the purchase is cash ("cash", "all cash", "cash purchase"). */
export function isCash(text: string): boolean {
  return /^(all[- ])?cash( (purchase|sale|offer|transaction|only|deal))?$/.test(text.trim().toLowerCase())
}

/** The form's financing choice, only for a plain FHA, VA or conventional loan ("" for "non-conventional",
 *  "conventional or FHA", or anything else the form has no single choice for). */
export function financingChoice(text: string): string {
  const t = text.trim().toLowerCase()
  if (/\bnon[- ]?|\bnot\b|\bor\b|\//.test(t)) return ''
  // More than one kind of loan named ("FHA and conventional", "conventional, FHA"): no single choice.
  // (A cash down payment on a loan is still that loan.)
  if (new Set(t.match(/\b(fha|va|conventional|usda|seller financ\w*)\b/g) ?? []).size > 1) return ''
  if (/^fha\b/.test(t) || /^(an? )?fha (insured )?loan$/.test(t)) return 'FHA'
  if (/^va\b/.test(t) || /^(an? )?va (guaranteed )?loan$/.test(t)) return 'VA'
  if (/^conventional\b/.test(t)) return 'Conventional'
  return ''
}

const emptyAddress: PartyFields['Address'] = { SameAsProperty: false, Address1: '', Address2: '', City: '', State: '', Zip: '' }

function toParty(p: ReaderParty): PartyFields {
  const orgType = (ORGANIZATION_TYPES as readonly string[]).includes(p.organization_type ?? '') ? p.organization_type as OrganizationType : ''
  const people = (p.contact_people ?? []).map(splitName)
  return {
    Type: p.type === 'married' || p.type === 'organization' ? p.type : 'individual',
    OrganizationName: p.type === 'organization' ? p.name : '',
    OrganizationType: p.type === 'organization' ? orgType : '',
    Primary: p.type === 'organization' ? splitName('') : { ...splitName(p.name), Email: p.email ?? '', Phone: p.phone ?? '' },
    Spouse: splitName(p.type === 'married' ? p.spouse : ''),
    OrgPeople: people.length ? people : [splitName('')],
    Address: { ...emptyAddress },
  }
}

/** One value the reader put on the form, for showing where it came from. */
export type Filled = {
  label: string
  value: string
  page: number | null
  /** null: the AI didn't rate it. */
  confidence: Confidence
  quote: string
  /** Not read straight from the contract: set by the panel (Type of Order) or matched to the user. */
  auto?: boolean
  /** Shown instead of the page and quote for an auto value. */
  why?: string
}

export type FormPatch = {
  /** Top-level form values to set. */
  values: Record<string, string | boolean>
  /** Each value's label in the "where each came from" list, by form field name. */
  labels: Record<string, string>
  buyers: PartyFields[]
  sellers: PartyFields[]
  lender: LenderInfo | null
  filled: Filled[]
}

/** Maps a reading onto this form. Values the contract doesn't give are left out; nothing is guessed. */
export function readingToForm(reading: Reading): FormPatch {
  const values: Record<string, string | boolean> = {}
  const labels: Record<string, string> = { OrderType: 'Type of Order' }
  const filled: Filled[] = []
  const field = (id: string) => reading.fields?.[id]
  type Src = ReaderField | { page: number | null; evidence?: string; confidence?: Confidence }
  const note = (label: string, value: string, src?: Src) =>
    filled.push({ label, value, page: src?.page ?? null, confidence: src?.confidence ?? null, quote: src?.evidence ?? '' })
  const set = (name: string, label: string, value: string | boolean, src?: Src, shown?: string) => {
    if (value === '' || value == null) return
    values[name] = value
    labels[name] = label
    note(label, shown ?? (typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value), src)
  }

  values.OrderType = 'Purchase'
  filled.push({ label: 'Type of Order', value: 'Purchase', page: null, confidence: null, quote: '', auto: true })
  const closing = formDate(field('closing_date')?.normalized)
  set('ClosingDate', 'Closing Date', closing, field('closing_date'), closing && showDate(closing))
  const contract = formDate(field('contract_date')?.normalized)
  set('ContractDate', 'Contract Date', contract, field('contract_date'), contract && showDate(contract))
  const financing = String(field('financing_type')?.value ?? '').toLowerCase()
  const cash = isCash(financing)
  // Only a plain cash or plain loan wording ticks or unticks the box; anything else is left for the agent.
  if (cash || financingChoice(financing)) set('IsCashOrder', 'This is a Cash Order', cash, field('financing_type'))

  // "1 Main St, Apt 4, Austin, TX": the street, and the unit when the contract didn't give it separately.
  const address = field('property_address')
  const parts = String(address?.value ?? '').split(',').map((x) => x.trim())
  set('PropertyAddress1', 'Address 1', parts[0] ?? '', address)
  const unitInAddress = /^(#\s*\S|(apt|apartment|unit|suite|ste|bldg|building|lot)\b)/i.test(parts[1] ?? '') ? parts[1] : ''
  if (field('property_unit')?.value) set('PropertyAddress2', 'Address 2', String(field('property_unit')?.value ?? ''), field('property_unit'))
  else set('PropertyAddress2', 'Address 2', unitInAddress, address)
  set('PropertyCity', 'City', String(field('property_city')?.value ?? ''), field('property_city'))
  const state = String(field('property_state')?.value ?? '').trim().toUpperCase()
  set('PropertyState', 'State', US_STATES.includes(state) ? state : '', field('property_state'))
  const zip = String(field('property_zip')?.value ?? '').trim()
  set('PropertyZip', 'Zip', /^\d{5}(-\d{4})?$/.test(zip) ? zip : '', field('property_zip'))
  set('PropertyLegalDescription', 'Legal Description', String(field('legal_description')?.value ?? ''), field('legal_description'))
  set('PropertyTaxId', 'Tax ID / Parcel Number', String(field('parcel_number')?.value ?? ''), field('parcel_number'))
  const type = String(field('property_type')?.value ?? '')
  set('PropertyType', 'Property Type', PROPERTY_TYPES.includes(type) ? type : '', field('property_type'))

  const buyers = (reading.buyers ?? []).map(toParty)
  const sellers = (reading.sellers ?? []).map(toParty)
  // Every party value that goes on the form gets its own source line.
  for (const [side, people, forms] of [['Buyer', reading.buyers ?? [], buyers], ['Seller', reading.sellers ?? [], sellers]] as const) {
    people.forEach((p, n) => {
      const at = `${side} ${n + 1}`
      const src = { page: p.page ?? null, evidence: p.evidence, confidence: p.confidence ?? null }
      note(at, p.type === 'married' && p.spouse ? `${p.name} and ${p.spouse}` : p.name, src)
      const f = forms[n]
      if (f.OrganizationType) note(`${at}: organization type`, f.OrganizationType, src)
      ;(p.contact_people ?? []).forEach((c, k) => note(`${at}: contact person ${k + 1}`, c, src))
      if (f.Primary.Email) note(`${at}: email`, f.Primary.Email, src)
      if (f.Primary.Phone) note(`${at}: phone`, f.Primary.Phone, src)
    })
  }

  let lender: LenderInfo | null = null
  if (!cash) {
    const loan = field('loan_amount')
    const amount = typeof loan?.normalized === 'number' && loan.normalized > 0 ? loan.normalized.toFixed(2) : ''
    // USDA, seller financing and the like have no choice on the form, so they're left for the agent.
    const choice = financingChoice(financing)
    const name = String(field('lender_name')?.value ?? '')
    if (amount || choice || name) {
      lender = { LenderName: name, LoanAmount: amount, LoanNumber: '', FinancingType: choice,
        LenderFirstName: '', LenderLastName: '', LenderEmail: '', LenderPhone: '' }
      if (name) filled.push({ label: 'Lender Name', value: name, page: field('lender_name')?.page ?? null, confidence: field('lender_name')?.confidence ?? null, quote: field('lender_name')?.evidence ?? '' })
      if (amount) filled.push({ label: 'Loan Amount', value: amount, page: loan?.page ?? null, confidence: loan?.confidence ?? null, quote: loan?.evidence ?? '' })
      if (choice) filled.push({ label: 'Financing Type', value: choice, page: field('financing_type')?.page ?? null, confidence: field('financing_type')?.confidence ?? null, quote: field('financing_type')?.evidence ?? '' })
    }
  }

  return { values, labels, buyers, sellers, lender, filled }
}

/** The logged-in user as the login gives them (Auth0's ID token). */
export type LoginUser = { email?: string; name?: string; given_name?: string; family_name?: string }

/** First and last name from the login, or null when it has none (an email-only login sets name to the email). */
export function loginName(user: LoginUser | undefined): { FirstName: string; LastName: string } | null {
  if (!user) return null
  if (user.given_name?.trim() && user.family_name?.trim()) return { FirstName: user.given_name.trim(), LastName: user.family_name.trim() }
  const name = user.name?.trim() ?? ''
  if (!name || name.includes('@') || !/\s/.test(name)) return null
  const { FirstName, LastName } = splitName(name)
  return { FirstName, LastName }
}

